import React from 'react';
import Head from 'next/head';

type PageSeoHeadProps = {
  seoTitle?: string;
  seoDescription?: string;
  hideFromSearch?: boolean;
  /**
   * Documented existing default (ECOMM-14849): the storefront's own name, matching the
   * fallback already established for dispensary-level metadata in
   * `src/dispensary/core-menu/components/header/header.tsx` (`titleWithFallback`). A blank
   * SEO title falls back to this rather than rendering an empty/broken `<title>`.
   */
  fallbackTitle: string;
};

/**
 * Published-page metadata for a CMS page's root (ECOMM-14849). Reads the author's SEO title/
 * description/hide-from-search root props and renders them as real `<head>` tags so they reach
 * the storefront through the same publication lifecycle as the rest of the page's content --
 * there is no separate metadata pipeline to keep in sync.
 *
 * A blank description renders no `<meta name="description">` tag at all (matching the existing
 * dispensary-level convention in `header.tsx`) rather than an empty one, which search engines
 * would otherwise treat as a deliberately blank summary instead of "no override, infer your own".
 *
 * `hideFromSearch` only ever ADDS a page-specific noindex/nofollow directive; it never emits an
 * explicit "index, follow" when off. That preserves any site-level robots rules a theme/host
 * page already sets elsewhere -- this component only ever restricts, never un-restricts.
 */
export function PageSeoHead({
  seoTitle,
  seoDescription,
  hideFromSearch,
  fallbackTitle,
}: PageSeoHeadProps): JSX.Element {
  const title = seoTitle?.trim() ? seoTitle : fallbackTitle;
  const description = seoDescription?.trim();

  return (
    <Head>
      {/* Always render the title tag so Next.js can update it, same as header.tsx. */}
      <title key='title'>{title}</title>
      {description && <meta name='description' content={description} />}
      {hideFromSearch === true && <meta name='robots' content='noindex, nofollow' />}
    </Head>
  );
}
